import React, { useState } from 'react';
import { Modal, Card, Typography, Space, Button } from 'antd';
import { 
  FileExcelOutlined, 
  ArrowRightOutlined
} from '@ant-design/icons';
import POSRequestForm from './POSRequestForm';

const { Title, Text } = Typography;

const ConnectionTypeSelector = ({ open, onClose, onSelectType }) => {
  const [isRequestFormOpen, setIsRequestFormOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const connectionTypes = [
    {
      key: 'spreadsheet',
      title: 'Upload Spreadsheet',
      description: 'Upload Excel or CSV files to create data connections',
      icon: <FileExcelOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      // features: ['Excel (.xlsx, .xls)', 'CSV files', 'Schema mapping', 'Data preview'],
      color: '#1890ff',
      type: 'file'
    },
    {
      key: 'clover',
      title: 'Clover POS',
      description: 'Connect to your Clover POS system for live data integration',
      icon: <div className="pos-provider-logo clover">Clover</div>,
      color: '#28a745',
      type: 'pos',
      supported: true
    },
    {
      key: 'square',
      title: 'Square POS',
      description: 'Connect to your Square POS system for business data',
      icon: <div className="pos-provider-logo square">Square</div>,
      color: '#000000',
      type: 'pos',
      supported: true
    },
    {
      key: 'par-brinks',
      title: 'PAR Brinks',
      description: 'Connect to your PAR Brinks POS system',
      icon: <div className="pos-provider-logo par-brinks">PAR<br/>Brinks</div>,
      color: '#0066cc',
      type: 'pos',
      supported: false
    },
    {
      key: 'olo',
      title: 'Olo',
      description: 'Connect to your Olo ordering platform',
      icon: <div className="pos-provider-logo olo">Olo</div>,
      color: '#ff6b35',
      type: 'pos',
      supported: false
    },
    {
      key: 'thanx',
      title: 'Thanx',
      description: 'Connect to your Thanx loyalty platform',
      icon: <div className="pos-provider-logo thanx">Thanx</div>,
      color: '#6c5ce7',
      type: 'pos',
      supported: false
    },
    {
      key: 'par-punchh',
      title: 'PAR Punchh',
      description: 'Connect to your PAR Punchh loyalty platform',
      icon: <div className="pos-provider-logo par-punchh">PAR<br/>Punchh</div>,
      color: '#e17055',
      type: 'pos',
      supported: false
    }
  ];

  const handleSelectType = (connectionType) => {
    if (connectionType.type === 'pos' && !connectionType.supported) {
      // Show request form for unsupported providers
      setSelectedProvider({
        key: connectionType.key,
        name: connectionType.title
      });
      setIsRequestFormOpen(true);
    } else {
      // Handle supported providers and file uploads
      onSelectType(connectionType.key);
      onClose();
    }
  };

  const handleCloseRequestForm = () => {
    setIsRequestFormOpen(false);
    setSelectedProvider(null);
  };

  return (
    <Modal
      title="Select Connection Type"
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <div style={{ padding: '20px 0' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={4}>Choose how you want to connect your data</Title>
          <Text type="secondary">
            Select the type of data source you want to connect to your workflows
          </Text>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {connectionTypes.map((type) => (
            <Card
              key={type.key}
              className={`pos-provider-card ${type.supported !== false ? 'supported' : 'not-supported'}`}
              hoverable={type.supported !== false}
              style={{
                textAlign: 'center',
                border: `2px solid ${type.supported === false ? '#f0f0f0' : '#f0f0f0'}`,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                opacity: type.supported === false ? 0.8 : 1
              }}
              bodyStyle={{ padding: '20px 12px' }}
              onClick={() => handleSelectType(type)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = type.color;
                e.currentTarget.style.boxShadow = `0 4px 12px ${type.color}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#f0f0f0';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{display: 'flex', justifyContent: 'center'}}>{type.icon}</div>
                
                <div>
                  <Title level={5} style={{ margin: '8px 0 4px 0', color: type.color, fontSize: 14 }}>
                    {type.title}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                    {type.description}
                  </Text>
                </div>

                {type.features && <div style={{ textAlign: 'left', width: '100%' }}>
                  <Text strong style={{ fontSize: 12, color: '#666' }}>File Formats:</Text>
                  <ul style={{ 
                    margin: '4px 0 0 0', 
                    padding: '0 0 0 16px', 
                    fontSize: 12, 
                    color: '#666',
                    lineHeight: '1.4'
                  }}>
                    {type.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>}

                <Button 
                  type="primary" 
                  icon={<ArrowRightOutlined />}
                  style={{ 
                    backgroundColor: type.color, 
                    borderColor: type.color,
                    marginTop: 8
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectType(type);
                  }}
                >
                  {type.supported === false ? 'Request' : 'Connect'}
                </Button>
              </Space>
            </Card>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Don't see your POS system? Click "Request" on any provider above to submit an integration request.
          </Text>
        </div>
      </div>

      {/* POS Integration Request Form */}
      <POSRequestForm 
        open={isRequestFormOpen}
        onClose={handleCloseRequestForm}
        provider={selectedProvider?.key}
        providerInfo={selectedProvider}
      />
    </Modal>
  );
};

export default ConnectionTypeSelector;